import React, { useEffect, useRef, useState } from "react";
import { useParams, Prompt, useLocation } from "react-router-dom";
import { Box, Grid, TextField, Backdrop, Switch, FormControlLabel } from "@material-ui/core";
import { useFormik } from "formik";
import * as Yup from "yup";
import { useAppDispatch } from "src/app-redux/hooks";
import { addNotificationAction } from "src/app-redux/settings/settingsSlice";
import CommonAutoCompleteField from "src/components/CommonAutoCompleteField/CommonAutoCompleteField";
import CommonButton from "src/components/CommonButton/CommonButton";
import { useStyles } from "./style";
import { fetchWrapper } from "src/api-client/fetchWrapper";
import TransitionDialog from "src/components/AlertDialog/TransitionDialog";
import { getPrintFile } from "src/app-redux/common-actions";
import moment from "moment";
import DuplicateReceiptDialog from "src/components/AlertDialog/DuplicateReceiptDialog";
import MultiSelect2 from "src/components/molecules/MultiSelect2";
import GateOutDetails from "./GateOutDetails";
import CommonLoader from "src/components/Loader/CommonLoader";
import { GridRowModel } from "@mui/x-data-grid-pro";

type Props = {
  isByd?: boolean;
};

function parseInvoiceHistoryRecords(results: any): any[] {
  const raw =
    results?.value?.records ??
    results?.records ??
    results?.value?.invoiceHistory ??
    results?.value ??
    results?.invoiceHistory ??
    results;

  return Array.isArray(raw) ? raw : [];
}

function mapInvoiceHistoryToChassisOptions(records: any[]): any[] {
  const seen = new Set<string>();

  return records
    .map((record) => {
      const chasisNumber = String(record?.chassisNumbers ?? record?.chasisNumbers ?? "").trim();
      if (!chasisNumber || seen.has(chasisNumber)) return null;

      seen.add(chasisNumber);
      return {
        chasisNumber,
        containerNumber: String(record?.containerNo ?? record?.containerNumber ?? "").trim(),
        id: record?.id,
      };
    })
    .filter(Boolean);
}

export default function GateOutInput({ isByd = false }: Props): React.ReactElement {
  const classes = useStyles();
  const dispatch = useAppDispatch();
  const [loader, setLoader] = useState<any>(false);
  const [, setLoader2] = useState<any>(false);
  const [selectDetails, setSelectDetails] = useState<any>({ vehicleList: {}, containerList: [] });
  const [isDirty, setIsDirty] = React.useState<boolean>(false);
  const [vehicleContanerNumbers, setVehicleContanerNumbers] = React.useState<any>([]);
  const [selectContainerIds, setSelectContainerIds] = useState<string[]>([]);
  const multiselectRef: any = useRef();
  const [isOpenPrintDailog, setIsOpenPrintDailog] = useState<boolean>(false);
  const params: any = useParams();
  const location = useLocation();
  const queryParams = new URLSearchParams(location.search);
  const container = queryParams.get("container");
  const [multiSelectKey, setMultiSelectKey] = useState(Date.now());
  const [vehiclePaymentDetails, setVehiclePaymentDetails] = React.useState<any>([]);
  const [isOpenCheckoutDailog, setIsOpenCheckoutDailog] = useState<boolean>(false);
  const terminalType = Number(localStorage.getItem("terminalType"));
  const [containerIndexes, setContainerIndexes] = useState<any[]>([]);
  const [isEditMode] = React.useState(false);
  const [containerList, setContainerList] = React.useState<any>([]);
  const [vehicleNumberLength, setVehicleNumberLength] = useState<number>(0);
  const [virNumber, setvirNumber] = React.useState<any[]>([]);
  const [, setGridRecord] = React.useState<GridRowModel[]>([]);
  const [indexContainerDetails, setvIndexContainerDetails] = useState({});
  const [showChassisSelect, setShowChassisSelect] = useState<boolean>(false);
  const [chassisOptions, setChassisOptions] = React.useState<any>([]);
  const [selectedChassis, setSelectedChassis] = React.useState<any>([]);
  const [selectedContainerIndexes, setSelectedContainerIndexes] = useState<any[]>([]);
  const [isOpenPaymentDialog, setIsOpenPaymentDialog] = useState<boolean>(false);
  const [blNumberOptions, setBlNumberOptions] = React.useState<any[]>([]);
  const [blNumberLoading, setBlNumberLoading] = useState<boolean>(false);
  const [bydChassisMultiSelectKey, setBydChassisMultiSelectKey] = useState(Date.now());

  const onMultiSelectSelection = (selectedList: any, selectedItem: any) => {
    setSelectDetails((prevState: any) => ({
      ...prevState,
      containerList: [...prevState?.containerList, selectedItem],
    }));

    const containerIds: string = selectedList
      .map((item: any) => (item?.gateEntryId ? item?.gateEntryId : ""))
      .toString();
    formik.setFieldValue("containerNumber", containerIds);

    // Only populate container details for terminalType === 4
    if (terminalType === 4) {
      // Fetch vehicle payment details if vehicle is selected
      if (formik.values.vehicleId) {
        getVehicleDetails(formik.values.vehicleId).then(() => {
          // Update containerList with selected container details
          const containerDetails = selectedList.map((item: any) => ({
            containerNumber: item?.containerNumber || "",
            vehicleRegNumber: item?.vehicleNumber || formik.values.vehicleNumber || "",
            cargoTypeName: item?.cargoTypeName || item?.cargoType || "-",
            containerSize: item?.containerSize || null,
            vessel: item?.vessel || "-",
            billNo: item?.billNo || "-",
            gdNo: item?.gdNo || "-",
            containerWebocWeight: item?.containerWebocWeight || item?.containerGrossWeight || null,
            weight: item?.weight || null,
            virNo: item?.virNo || "-",
            createdDateTime: item?.createdDateTime || item?.gateEntryDate || null,
            importer: item?.importer || "_",
          }));

          setVehiclePaymentDetails((prev: any) => ({
            ...prev,
            containerList: containerDetails,
          }));
        });
      } else {
        // If no vehicle selected, still set container details structure
        const containerDetails = selectedList.map((item: any) => ({
          containerNumber: item?.containerNumber || "",
          vehicleRegNumber: item?.vehicleNumber || "",
          cargoTypeName: item?.cargoTypeName || item?.cargoType || "-",
          containerSize: item?.containerSize || null,
          vessel: item?.vessel || "-",
          billNo: item?.billNo || "-",
          gdNo: item?.gdNo || "-",
          containerWebocWeight: item?.containerWebocWeight || item?.containerGrossWeight || null,
          weight: item?.weight || null,
          virNo: item?.virNo || "-",
          createdDateTime: item?.createdDateTime || item?.gateEntryDate || null,
          importer: item?.importer || "_",
        }));

        setVehiclePaymentDetails((prev: any) => ({
          ...prev,
          containerList: containerDetails,
        }));
      }
    }
  };

  const onMultiSelectRemove = (selectedList: any, removedItem: any) => {
    setSelectDetails((prevState: any) => ({
      ...prevState,
      containerList: prevState?.containerList.filter(
        (item: any) => item?.gateEntryId !== removedItem?.gateEntryId
      ),
    }));
    const containerIds: string = selectedList
      .map((item: any) => (item.gateEntryId ? item.gateEntryId : ""))
      .toString();
    formik.setFieldValue("containerNumber", containerIds);

    // Only update containerList for terminalType === 4
    if (terminalType === 4) {
      // Update containerList in vehiclePaymentDetails when container is removed
      if (selectedList.length > 0) {
        const containerDetails = selectedList.map((item: any) => ({
          containerNumber: item?.containerNumber || "",
          vehicleRegNumber: item?.vehicleNumber || formik.values.vehicleNumber || "",
          cargoTypeName: item?.cargoTypeName || item?.cargoType || "-",
          containerSize: item?.containerSize || null,
          vessel: item?.vessel || "-",
          billNo: item?.billNo || "-",
          gdNo: item?.gdNo || "-",
          containerWebocWeight: item?.containerWebocWeight || item?.containerGrossWeight || null,
          weight: item?.weight || null,
          virNo: item?.virNo || "-",
          createdDateTime: item?.createdDateTime || item?.gateEntryDate || null,
        }));

        setVehiclePaymentDetails((prev: any) => ({
          ...prev,
          containerList: containerDetails,
        }));
      } else {
        // If no containers selected, clear containerList
        setVehiclePaymentDetails((prev: any) => ({
          ...prev,
          containerList: [],
        }));
      }
    }
  };

  const validationSchema = Yup.object().shape({
    remarks: Yup.string().max(3000, "Maximum 3000 characters allowed"),
  });
  const getContainerListing = async (vir: string | number) => {
    setLoader(true);
    try {
      const results = await fetchWrapper.post(`get-all-vir-containers/${vir}`, {});
      if (results.isSuccess == true) {
        setContainerList(results.value?.continers);
      }
      setLoader(false);
      return;
    } catch (error: any) {
      setLoader(false);
      dispatch(
        addNotificationAction({
          message: error.message,
          options: { variant: "error" },
        })
      );
    }
  };
  const fetchChassisByIndex = async () => {
    try {
      setLoader(true);
      const { virNumber, containerNumberIndex, containerIndexes } = formik.values;
      const firstContainerIndex = Number(containerIndexes?.split(",")[0]);
      const response = await fetchWrapper.get(
        `/get-all-containers-index_chasis_numbers/${containerNumberIndex}?virNo=${virNumber}&indexNumber=${firstContainerIndex}`
      );

      if (response.isSuccess) {
        setChassisOptions(response.value.chasisNumbers || []);
      } else {
        dispatch(
          addNotificationAction({
            message: "Failed to fetch chassis numbers",
            options: { variant: "error" },
          })
        );
      }
    } catch (error: any) {
      dispatch(
        addNotificationAction({
          message: error.message,
          options: { variant: "error" },
        })
      );
    } finally {
      setLoader(false);
    }
  };

  const getvirNumber = async (virNo: string) => {
    let results: any = [];
    results = await fetchWrapper.get(`search-vir-number/${virNo}`);
    if (results.isSuccess) {
      setvirNumber(results.value.virList);
    }
    return;
  };
  const getListing = async (containerNumber: string | number, virNo: string | number) => {
    setLoader2(true);
    try {
      const results = await fetchWrapper.get(
        `get-all-containers-paid_indexes/${containerNumber}?virNo=${virNo}`
      );

      if (results.isSuccess === true) {
        setGridRecord(results?.value?.containerDetails);

        // Extract paidIndexes from response and set them
        const indexes = results?.value?.paidIndexes || [];
        const mappedIndexes = indexes.map((item: any) => ({
          id: item.indexNumber,
          indexName: item.indexNumber.toString(), // or format as needed
        }));
        setContainerIndexes(mappedIndexes); // <-- set options for Container Index
      }

      setLoader2(false);
    } catch (error: any) {
      setLoader2(false);
      dispatch(
        addNotificationAction({
          message: error.message,
          options: { variant: "error" },
        })
      );
    }
  };

  const formik = useFormik({
    initialValues: {
      vehicleNumber: "",
      vehicleId: "",
      gateEntryId: "",
      containerNumber: "",
      remarks: "",
      containerIndex: [],
      virNumber: "",
      registrationNumber: "",
      containerNumberIndex: "",
      chassisNumbers: "",
      containerIndexes: "", // store selected IDs
      containerIndexesDisplay: "", // store display string (read-only input)
      agentNTN: "",
      agentName: "",
      agencyName: "",
      billNumber: "",
    },
    validationSchema: validationSchema,
    onSubmit: async () => {
      //
    },
  });

  const getVehicleContainerNumbers = async () => {
    setLoader(true);
    const results = await fetchWrapper.get("get-all-vehicles-and-containers-for-gate-out");
    setLoader(false);

    if (results.isSuccess) {
      setVehicleContanerNumbers(results.value);
    }
    return;
  };

  const getVehicleDetails = async (vehicleId: any) => {
    setLoader(true);
    try {
      const results = await fetchWrapper.get(`get-vehicle-payment-details/${vehicleId}`);
      setVehiclePaymentDetails(results?.value);
      setLoader(false);
      return results?.value;
    } catch (error) {
      setLoader(false);
      return null;
    }
  };
  const getIndexContainerDetails = async (gatePassId: any) => {
    setLoader(true);
    const results = await fetchWrapper.get(`/get-gate-entry-vehicle-detail-by-id/${gatePassId}`);
    setvIndexContainerDetails(results?.value?.vehicleDetail || {});

    setLoader(false);
  };

  const resetVehicleAndContainerFields = () => {
    formik.setFieldValue("vehicleNumber", "");
    formik.setFieldValue("vehicleId", "");
    formik.setFieldValue("containerNumber", "");
    formik.setFieldValue("gateEntryId", "");
    setSelectContainerIds([]);
    setSelectDetails({ vehicleList: {}, containerList: [] });
    setMultiSelectKey(Date.now());
    setVehiclePaymentDetails([]);
  };

  const getPendingBillNumberOptions = async (search: string) => {
    const query = search?.trim();
    if (!query || query.length < 2) {
      setBlNumberOptions([]);
      return;
    }

    setBlNumberLoading(true);
    try {
      const results = await fetchWrapper.get(
        `get-pending-bill-numbers?search=${encodeURIComponent(query)}&checkout=false`
      );
      const billNumbers = results?.value?.billNumbers ?? results?.billNumbers;

      if (Array.isArray(billNumbers) && billNumbers.length > 0) {
        setBlNumberOptions(billNumbers);
      } else {
        setBlNumberOptions([]);
      }
    } catch {
      setBlNumberOptions([]);
    } finally {
      setBlNumberLoading(false);
    }
  };

  const resetBydChassisSelection = () => {
    setChassisOptions([]);
    setSelectedChassis([]);
    formik.setFieldValue("chassisNumbers", "");
    setBydChassisMultiSelectKey(Date.now());
  };

  const fetchInvoiceHistoryByBillNumber = async (billNumber: string) => {
    const search = billNumber?.trim();
    if (!search) {
      resetBydChassisSelection();
      return [];
    }

    const results = await fetchWrapper.get(
      `get-invoice-history-by-bill-number?billNumber=${encodeURIComponent(search)}&checkout=false`
    );
    const options = mapInvoiceHistoryToChassisOptions(parseInvoiceHistoryRecords(results));
    setChassisOptions(options);
    setSelectedChassis([]);
    formik.setFieldValue("chassisNumbers", "");
    setBydChassisMultiSelectKey(Date.now());
    return options;
  };

  const handleBlNumberSelect = async (billNumber: string) => {
    const search = billNumber?.trim();
    if (!search) {
      formik.setFieldValue("billNumber", "");
      resetBydChassisSelection();
      return;
    }

    setLoader(true);
    try {
      const results = await fetchWrapper.get(
        `get-pending-bill-numbers?search=${encodeURIComponent(search)}&checkout=false`
      );
      const billNumbers = results?.value?.billNumbers ?? results?.billNumbers;
      const hasBillNumbers = Array.isArray(billNumbers) && billNumbers.length > 0;

      if (results?.isSuccess || hasBillNumbers) {
        const resolvedBillNumber =
          billNumbers.find((item: any) => String(item?.billNumber ?? "").trim() === search)
            ?.billNumber ??
          billNumbers[0]?.billNumber ??
          search;
        const trimmedBillNumber = String(resolvedBillNumber ?? "").trim();
        formik.setFieldValue("billNumber", trimmedBillNumber);
        try {
          await fetchInvoiceHistoryByBillNumber(trimmedBillNumber);
        } catch {
          resetBydChassisSelection();
        }
        return;
      }

      formik.setFieldValue("billNumber", "");
      resetBydChassisSelection();
      dispatch(
        addNotificationAction({
          message: results?.errors?.[0] || "No pending bill numbers found.",
          options: { variant: "error" },
        })
      );
    } catch (error: any) {
      formik.setFieldValue("billNumber", "");
      resetBydChassisSelection();
      dispatch(
        addNotificationAction({
          message: error.message,
          options: { variant: "error" },
        })
      );
    } finally {
      setLoader(false);
    }
  };

  const handleBydChassisSelect = (selectedList: any[]) => {
    setSelectedChassis(selectedList);
    formik.setFieldValue(
      "chassisNumbers",
      selectedList
        .map((item) => item?.chasisNumber)
        .filter(Boolean)
        .join(",")
    );
    setIsDirty(true);
  };

  function handleChange(e: any) {
    formik.handleChange(e);
    e.target.value || JSON.stringify(formik.values) !== JSON.stringify(formik.initialValues)
      ? setIsDirty(true)
      : setIsDirty(false);
  }

  // Helper function to check if any selected container has payment not completed
  const checkPaymentStatus = () => {
    if (!formik.values.containerNumber) {
      return true; // No container selected, allow to proceed (or handle as needed)
    }

    const containerIds = formik.values.containerNumber
      .toString()
      .split(",")
      .map((id) => id.trim());

    // Check each selected container's payment status
    for (const containerId of containerIds) {
      const container = vehicleContanerNumbers?.containerList?.find(
        (item: any) => item.gateEntryId?.toString() === containerId
      );
      if (container && container.isPaymentCompleted === 0) {
        return false; // Payment not completed
      }
    }

    return true; // All containers have payment completed
  };

  const handleCheckout = async () => {
    if (isByd) {
      if (!formik.values.billNumber?.trim()) {
        dispatch(
          addNotificationAction({
            message: "Please select a valid BL Number first.",
            options: { variant: "error" },
          })
        );
        return;
      }
      if (!formik.values.chassisNumbers?.trim()) {
        dispatch(
          addNotificationAction({
            message: "Please select at least one Chassis Number.",
            options: { variant: "error" },
          })
        );
        return;
      }
    } else if (!formik.values.vehicleId) {
      dispatch(
        addNotificationAction({
          message: "Please select Vehicle Number first.",
          options: { variant: "error" },
        })
      );
      return;
    }

    // Check if payment is completed for selected containers (only for terminalType === 4)
    if (terminalType === 4 && !checkPaymentStatus()) {
      setIsOpenPaymentDialog(true);
      return;
    }

    const allChassisGateOut = !(selectedChassis.length < chassisOptions.length);

    const selectedGateEntryIds = formik.values.containerNumber
      .toString()
      .split(",")
      .map((id) => id.trim())
      .filter(Boolean);

    const selectedContainerNumbers = selectedGateEntryIds
      .map(
        (gateEntryId) =>
          vehicleContanerNumbers?.containerList?.find(
            (item: any) => item.gateEntryId?.toString() === gateEntryId
          )?.containerNumber
      )
      .filter(Boolean)
      .join(",");

    const body = {
      gatePassIds: formik.values.containerNumber,
      vehicleId: formik.values.vehicleId,
      vehicleNumber: formik.values.vehicleNumber,
      containerNumber: selectedContainerNumbers,
      indexContainerNumber: formik.values.containerNumberIndex,
      indexes: formik.values.containerIndexes,
      virNumber: formik.values.virNumber,
      chassisNumbers: formik.values.chassisNumbers,
      gateOutIndex: allChassisGateOut,
      agentNTN: formik.values.agentNTN,
      agentName: formik.values.agentName,
      agencyName: formik.values.agencyName,
      ...(isByd ? { billNumber: formik.values.billNumber } : {}),
    };
    setLoader(true);
    const res = await fetchWrapper.put("gate-out-vehicle-and_container", body);
    setLoader(false);
    if (res) {
      formik.resetForm();
      formik.setFieldValue("vehicleNumber", "");
      formik.setFieldValue("vehicleId", "");
      formik.setFieldValue("containerNumber", "");
      formik.setFieldValue("virNumber", "");
      formik.setFieldValue("containerNumberIndex", "");
      formik.setFieldValue("chassisNumbers", "");
      formik.setFieldValue("containerIndexes", ""); // Reset containerIndexes formik field
      formik.setFieldValue("containerIndexesDisplay", "");
      formik.setFieldValue("agentNTN", "");
      formik.setFieldValue("agentName", "");
      formik.setFieldValue("agencyName", "");
      formik.setFieldValue("billNumber", "");
      setBlNumberOptions([]);
      setBydChassisMultiSelectKey(Date.now());
      setSelectedContainerIndexes([]);
      setSelectContainerIds([]);
      setSelectDetails({ vehicleList: {}, containerList: [] });
      setMultiSelectKey(Date.now());
      setvIndexContainerDetails(null as any);
      getVehicleContainerNumbers();
      setVehiclePaymentDetails([]);
      setIsDirty(false);
      setShowChassisSelect(false);
      setChassisOptions([]);
      setSelectedChassis([]);
      setContainerIndexes([]);
    }
  };

  const handleCloseCheckoutDialog = () => {
    setIsOpenCheckoutDailog(false);
  };

  const handleCheckoutDialog = async () => {
    setIsOpenCheckoutDailog(false);
    handleCheckout();
  };

  const listener = (e: any) => {
    if (
      (e.key === "s" || e.key === "S") &&
      (navigator.platform.match("Mac") ? e.metaKey : e.ctrlKey)
    ) {
      e.preventDefault();
      setIsOpenCheckoutDailog(true);
    }
  };

  const printReceipt = async (isDuplicate: string) => {
    setIsOpenPrintDailog(false);
    const dateTime = moment().format("DD MMM YYYY, HH:mm");
    const fileMetaData = {
      url: `${process.env.REACT_APP_API_URL}/get-gate-out-slip-by-gate-pass-id/${formik.values.gateEntryId}?isDuplicate=${isDuplicate}&isNod=true&printDateTime=${dateTime}`,
      contentType: ".pdf",
      fileName: "testPdf.pdf",
    };
    await dispatch(getPrintFile(fileMetaData, dispatch));
  };

  useEffect(() => {
    if (isByd) {
      resetVehicleAndContainerFields();
    } else {
      formik.setFieldValue("billNumber", "");
      setBlNumberOptions([]);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isByd]);

  useEffect(() => {
    getVehicleContainerNumbers();
    document.addEventListener("keydown", listener);
    return () => {
      document.removeEventListener("keydown", listener);
    };
  }, []);

  useEffect(() => {
    // Map containerIndexes to their ids and update selectContainerIds
    const ids = containerIndexes.map((item) => item.id);
    setSelectContainerIds(ids);
  }, [containerIndexes]);

  useEffect(() => {
    if (container == "true" && params) {
      const option = vehicleContanerNumbers?.containerList?.find(
        (st: any) => st.gateEntryId == params.id
      );

      if (option) {
        const newContainerId = option;

        setSelectDetails((prevState: any) => ({
          ...prevState,
          containerList: [...prevState.containerList, option],
        }));
        setSelectContainerIds((prev) => [...prev, newContainerId]);
        formik.setFieldValue("containerNumber", params.id);
      }
    } else {
      formik.setFieldValue("vehicleId", params.id);
      const option: any = vehicleContanerNumbers?.vehicleList?.find(
        (st: any) => st.vehicleId == params.id
      );
      formik.setFieldValue("vehicleNumber", option?.vehicleNumber);
      setSelectDetails((prevState: any) => ({
        ...prevState,
        vehicleList: option,
      }));
    }
  }, [vehicleContanerNumbers]);

  useEffect(() => {
    window.onbeforeunload = function (e: Event) {
      if (isDirty) {
        e.preventDefault();
        return "Changes you made may not be saved.";
      }
    };
  }, [isDirty]);

  // Compute if all chassis are selected for gate out
  const allChassisGateOut = !(selectedChassis.length < chassisOptions.length);

  return (
    <>
      <Prompt when={isDirty} message="Changes you made may not be saved." />
      <Backdrop className={classes.backdrop} open={loader}>
        <CommonLoader />
      </Backdrop>

      <Grid container spacing={2}>
        {isByd ? (
          <>
            <Grid item xs={4}>
              <Box>
                <CommonAutoCompleteField
                  cssClassName="customTextField"
                  label="BL Number"
                  placeholder="Enter BL Number"
                  name="billNumber"
                  optionsType={blNumberOptions}
                  loading={blNumberLoading}
                  getOptionLabel={(option: any) => option?.billNumber?.toString() || ""}
                  value={
                    blNumberOptions.find(
                      (item: any) =>
                        String(item?.billNumber ?? "") === String(formik.values.billNumber ?? "")
                    ) ||
                    (formik.values.billNumber ? { billNumber: formik.values.billNumber } : null)
                  }
                  onChange={(option: any) => {
                    if (!option) {
                      formik.setFieldValue("billNumber", "");
                      setBlNumberOptions([]);
                      resetBydChassisSelection();
                      return;
                    }
                    handleBlNumberSelect(String(option?.billNumber ?? ""));
                  }}
                  onKeyUp={(e: any) => {
                    const nextLen = e.target.value.length;
                    if (nextLen >= 2) {
                      getPendingBillNumberOptions(e.target.value);
                    } else {
                      setBlNumberOptions([]);
                    }
                  }}
                  onKeyDownCapture={(e: any) => {
                    if (/[^A-Za-z0-9\d]/.test(e.key)) e.preventDefault();
                  }}
                  onPaste={(e: any) => {
                    const pasted = e.clipboardData.getData("text");
                    if (/[^A-Za-z0-9\d]/.test(pasted)) {
                      e.preventDefault();
                      return;
                    }
                    if (pasted.length >= 2) {
                      getPendingBillNumberOptions(pasted);
                    }
                  }}
                />
              </Box>
            </Grid>
            <Grid item xs={4}>
              <Box>
                <TextField
                  className="customTextField"
                  size="small"
                  fullWidth
                  variant="outlined"
                  InputLabelProps={{ shrink: true }}
                  inputProps={{ readOnly: true }}
                  label="Chassis Number"
                  name="chassisNumbers"
                  value={formik.values.chassisNumbers || ""}
                  autoComplete="off"
                />
                <Box
                  className={classes.multiselectConfigFileCompression}
                  style={{ bottom: "35px" }}
                >
                  <MultiSelect2
                    allValues={chassisOptions}
                    selctedItems={selectedChassis}
                    displayValue="chasisNumber"
                    showSelectAll={chassisOptions.length > 0}
                    selectAllLabel="Select All"
                    closeOnSelect={false}
                    placeholder={
                      !formik.values.billNumber?.trim()
                        ? "Select BL Number first"
                        : chassisOptions.length
                        ? "Select Chassis Number(s)"
                        : "No chassis numbers found"
                    }
                    onSelect={handleBydChassisSelect}
                    onRemove={handleBydChassisSelect}
                    key={bydChassisMultiSelectKey}
                  />
                </Box>
              </Box>
            </Grid>
          </>
        ) : (
          <>
            <Grid item xs={4}>
              <Box>
                <CommonAutoCompleteField
                  cssClassName="customTextField"
                  label="Vehicle Number"
                  placeholder="Enter Vehicle Number"
                  name="vehicleNumber"
                  optionsType={vehicleContanerNumbers?.vehicleList}
                  getOptionLabel={(option) => option.vehicleNumber}
                  required
                  value={
                    vehicleContanerNumbers?.vehicleList?.find(
                      (st: any) => st.vehicleId == formik.values.vehicleId
                    ) || null
                  }
                  onChange={(option: any) => {
                    setSelectDetails((prevState: any) => ({
                      ...prevState,
                      vehicleList: option,
                    }));
                    if (terminalType === 4) {
                      const selectedContainer = vehicleContanerNumbers?.containerList?.find(
                        (ct: any) =>
                          ct.gateEntryId?.toString() === formik.values.containerNumber?.toString()
                      );
                      getVehicleDetails(option?.vehicleId).then(() => {
                        if (selectedContainer) {
                          setVehiclePaymentDetails((prev: any) => ({
                            ...prev,
                            containerList: [
                              {
                                containerNumber: selectedContainer?.containerNumber || "",
                                vehicleRegNumber: selectedContainer?.vehicleRegNumber || "",
                                cargoTypeName: selectedContainer?.cargoTypeName || "-",
                                containerSize: selectedContainer?.containerSize || null,
                                vessel: selectedContainer?.vessel || "-",
                                billNo: selectedContainer?.billNo || "-",
                                gdNo: selectedContainer?.gdNo || "-",
                                containerWebocWeight:
                                  selectedContainer?.containerWebocWeight ?? null,
                                weight: selectedContainer?.weight ?? null,
                                virNo: selectedContainer?.virNo || "-",
                                createdDateTime: selectedContainer?.createdDateTime || null,
                                importer: selectedContainer?.importer || "-",
                              },
                            ],
                          }));
                        }
                      });
                    }
                    formik.setFieldValue("gateEntryId", option?.gateEntryId);
                    formik.setFieldValue("vehicleId", option?.vehicleId);
                    formik.setFieldValue("vehicleNumber", option?.vehicleNumber);
                  }}
                  onKeyDownCapture={(e: any) => {
                    if (/[^A-Za-z0-9\d]/.test(e.key)) e.preventDefault();
                    else handleChange(e);
                  }}
                  onPaste={(e: any) => {
                    if (/[^A-Za-z0-9\d]/.test(e.clipboardData.getData("text"))) {
                      e.preventDefault();
                      return;
                    }
                  }}
                  error={formik.touched.vehicleNumber && Boolean(formik.errors.vehicleNumber)}
                  helperText={formik.touched.vehicleNumber && formik.errors.vehicleNumber}
                />
              </Box>
            </Grid>
            <Grid item xs={4}>
              <Box>
                {terminalType === 4 ? (
                  // MULTI-SELECT CONTAINER FIELD (for terminalType === 4)
                  <>
                    <TextField
                      required={false}
                      className="customTextField"
                      size="small"
                      fullWidth
                      variant="outlined"
                      InputLabelProps={{ shrink: true }}
                      onKeyDown={(event: any) => {
                        if (event.key !== "Tab") event.preventDefault();
                      }}
                      value={formik.values.containerNumber}
                      label="Container Number"
                      name="containerNumber"
                      autoComplete="off"
                      error={
                        formik.touched.containerNumber && Boolean(formik.errors.containerNumber)
                      }
                      helperText={formik.touched.containerNumber && formik.errors.containerNumber}
                    />
                    <Box
                      className={classes.multiselectConfigFileCompression}
                      style={{
                        bottom:
                          formik.touched.containerNumber && Boolean(formik.errors.containerNumber)
                            ? "55px"
                            : "35px",
                      }}
                    >
                      <MultiSelect2
                        allValues={vehicleContanerNumbers?.containerList}
                        selctedItems={selectContainerIds}
                        displayValue={"containerNumber"}
                        placeholder="Select Container Number"
                        onSelect={onMultiSelectSelection}
                        onRemove={onMultiSelectRemove}
                        ref={multiselectRef}
                        key={multiSelectKey}
                      />
                    </Box>
                  </>
                ) : (
                  // SINGLE-SELECT CONTAINER FIELD (for terminalType !== 4)
                  <CommonAutoCompleteField
                    cssClassName="customTextField"
                    label="Container Number To Gate Out"
                    placeholder="Select Container Number"
                    name="containerNumber"
                    optionsType={vehicleContanerNumbers?.containerList}
                    getOptionLabel={(option) => option.containerNumber}
                    required={false}
                    value={
                      vehicleContanerNumbers?.containerList?.find(
                        (ct: any) =>
                          ct.gateEntryId?.toString() === formik.values.containerNumber?.toString()
                      ) || null
                    }
                    onChange={(option: any) => {
                      setSelectDetails((prevState: any) => ({
                        ...prevState,
                        singleContainer: option,
                      }));

                      const selectedContainerNumber = option?.containerNumber || "";
                      const selectedGateEntryId = option?.gateEntryId?.toString() || "";

                      formik.setFieldValue("containerNumber", selectedGateEntryId);

                      const containerDetails = option
                        ? [
                            {
                              containerNumber: option?.containerNumber || "",
                              vehicleRegNumber: option?.vehicleRegNumber || "",
                              cargoTypeName: option?.cargoTypeName || "-",
                              containerSize: option?.containerSize || null,
                              vessel: option?.vessel || "-",
                              billNo: option?.billNo || "-",
                              gdNo: option?.gdNo || "-",
                              containerWebocWeight: option?.containerWebocWeight ?? null,
                              weight: option?.weight ?? null,
                              virNo: option?.virNo || "-",
                              createdDateTime: option?.createdDateTime || null,
                              importer: option?.importer || "-",
                            },
                          ]
                        : [];

                      const applyContainerDetails = () => {
                        setVehiclePaymentDetails((prev: any) => ({
                          ...prev,
                          containerList: containerDetails,
                        }));
                      };

                      applyContainerDetails();

                      if (selectedContainerNumber && formik.values.virNumber) {
                        getListing(selectedContainerNumber, formik.values.virNumber);
                      }
                    }}
                    onKeyDownCapture={(e: any) => {
                      if (/[^A-Za-z0-9\d]/.test(e.key)) e.preventDefault();
                    }}
                    onPaste={(e: any) => {
                      if (/[^A-Za-z0-9\d]/.test(e.clipboardData.getData("text"))) {
                        e.preventDefault();
                      }
                    }}
                    error={formik.touched.containerNumber && Boolean(formik.errors.containerNumber)}
                    helperText={formik.touched.containerNumber && formik.errors.containerNumber}
                  />
                )}
              </Box>
            </Grid>
          </>
        )}
        {/* {terminalType !== 4 && (
          <Grid item xs={12} md={4} lg={4}>
            <Box style={{ display: isEditMode ? "none" : "block" }}>
              <CommonAutoCompleteField
                cssClassName="customTextField"
                label="VIR Number"
                placeholder="Select VIR Number"
                name="virNumber"
                optionsType={virNumber}
                getOptionLabel={(option) => option.virNo || ""}
                value={virNumber.find((st: any) => st.virNo === formik.values.virNumber) || null}
                onChange={(option: any) => {
                  formik.setFieldValue("virNumber", option.virNo);
                  getContainerListing(option?.virNo);
                }}
                onKeyUp={(e: any) => {
                  setVehicleNumberLength(e.target.value.length);

                  if (e.target.value.length == 2 && vehicleNumberLength < 2) {
                    getvirNumber(e.target.value);
                    return;
                  }
                }}
                onKeyDownCapture={(e: any) => {
                  if (/[^A-Za-z0-9\d]/.test(e.key)) e.preventDefault();
                  else handleChange(e);
                }}
                onPaste={(e: any) => {
                  if (/[^A-Za-z0-9\d]/.test(e.clipboardData.getData("text"))) {
                    e.preventDefault();
                    return;
                  }

                  if (e.clipboardData.getData("text").length >= 2)
                    getvirNumber(e.clipboardData.getData("text"));
                }}
                error={
                  formik.touched.registrationNumber && Boolean(formik.errors.registrationNumber)
                }
                helperText={formik.touched.registrationNumber && formik.errors.registrationNumber}
              />
            </Box>
          </Grid>
        )} */}
        {terminalType === 4 && (
          <>
            <Grid item xs={12} md={4}>
              <Box>
                <TextField
                  size="small"
                  fullWidth
                  className="customTextField"
                  variant="outlined"
                  InputLabelProps={{
                    shrink: true,
                  }}
                  label="Agent Name"
                  name="agentName"
                  id="agentName"
                  placeholder="Enter Agent Name"
                  disabled={false}
                  value={formik.values.agentName}
                  onChange={handleChange}
                  onPaste={(e: any) => {
                    if (/[^A-Za-z0-9-,_ ]/.test(e.clipboardData.getData("text")))
                      e.preventDefault();
                  }}
                  onKeyDownCapture={(e: any) => {
                    if (/[^A-Za-z0-9-,_ ]/.test(e.key)) e.preventDefault();
                  }}
                  error={formik.touched.agentName && Boolean(formik.errors.agentName)}
                  helperText={formik.touched.agentName && formik.errors.agentName}
                />
              </Box>
            </Grid>
            <Grid item xs={12} md={4}>
              <Box>
                <TextField
                  size="small"
                  fullWidth
                  className="customTextField"
                  variant="outlined"
                  InputLabelProps={{
                    shrink: true,
                  }}
                  label="Agency Name"
                  name="agencyName"
                  id="agencyName"
                  placeholder="Enter Agency Name"
                  disabled={false}
                  value={formik.values.agencyName}
                  onChange={handleChange}
                  onPaste={(e: any) => {
                    if (/[^A-Za-z0-9-,_ ]/.test(e.clipboardData.getData("text")))
                      e.preventDefault();
                  }}
                  onKeyDownCapture={(e: any) => {
                    if (/[^A-Za-z0-9-,_ ]/.test(e.key)) e.preventDefault();
                  }}
                  error={formik.touched.agencyName && Boolean(formik.errors.agencyName)}
                  helperText={formik.touched.agencyName && formik.errors.agencyName}
                />
              </Box>
            </Grid>
            <Grid item xs={12} md={4}>
              <Box>
                <TextField
                  size="small"
                  fullWidth
                  className="customTextField"
                  variant="outlined"
                  InputLabelProps={{
                    shrink: true,
                  }}
                  label="Agent NTN Number"
                  name="agentNTN"
                  id="agentNTN"
                  placeholder="Enter Agent NTN Number"
                  disabled={false}
                  value={formik.values.agentNTN}
                  onChange={handleChange}
                  onPaste={(e: any) => {
                    if (/[^A-Za-z0-9-,_ ]/.test(e.clipboardData.getData("text")))
                      e.preventDefault();
                  }}
                  onKeyDownCapture={(e: any) => {
                    if (/[^A-Za-z0-9-,_ ]/.test(e.key)) e.preventDefault();
                  }}
                  error={formik.touched.agentNTN && Boolean(formik.errors.agentNTN)}
                  helperText={formik.touched.agentNTN && formik.errors.agentNTN}
                />
              </Box>
            </Grid>
          </>
        )}
        {/* {terminalType !== 4 && (
          <>
            <Grid item xs={12} md={4} lg={4}>
              <Box>
                <CommonAutoCompleteField
                  required
                  cssClassName="customTextField"
                  label="Index Container Number"
                  name="containerNumberIndex"
                  optionsType={containerList}
                  placeholder="Select Container Number"
                  getOptionLabel={(option: any) => option.containerNumber || ""}
                  value={containerList?.find(
                    (st: any) => st.containerNumber === formik.values.containerNumberIndex
                  )}
                  onChange={(option: any) => {
                    getListing(option.containerNumber, formik.values.virNumber);
                    getIndexContainerDetails(option.gatePassId);
                    formik.setFieldValue("containerNumberIndex", option.containerNumber);
                  }}
                  error={formik.touched.containerNumber && Boolean(formik.errors.containerNumber)}
                  helperText={formik.touched.containerNumber && formik.errors.containerNumber}
                />
              </Box>
            </Grid>

            <Grid item xs={12} md={6} lg={4}>
              <>
                <TextField
                  required
                  className="customTextField"
                  size="small"
                  fullWidth
                  variant="outlined"
                  InputLabelProps={{ shrink: true }}
                  inputProps={{ readOnly: true }} // Prevent manual input
                  label="Container Index"
                  name="containerIndexes"
                  autoComplete="off"
                  value={formik.values.containerIndexesDisplay || ""}
                  error={formik.touched.containerIndexes && Boolean(formik.errors.containerIndexes)}
                  helperText={formik.touched.containerIndexes && formik.errors.containerIndexes}
                />

                <Box
                  className={classes.multiselectConfigFileCompression}
                  style={{
                    bottom:
                      formik.touched.containerIndexes && Boolean(formik.errors.containerIndexes)
                        ? "55px"
                        : "35px",
                  }}
                >
                  <MultiSelect2
                    allValues={containerIndexes || []}
                    selctedItems={selectedContainerIndexes}
                    displayValue="indexName"
                    placeholder="Select Container Index"
                    onSelect={(selectedList: any[]) => {
                      setSelectedContainerIndexes(selectedList);
                      formik.setFieldValue(
                        "containerIndexes",
                        selectedList.map((item) => item.indexName).join(", ")
                      );
                    }}
                    onRemove={(selectedList: any[]) => {
                      setSelectedContainerIndexes(selectedList);
                      formik.setFieldValue(
                        "containerIndexes",
                        selectedList.map((item) => item.indexName).join(", ")
                      );
                    }}
                    ref={multiselectRef}
                    key={multiSelectKey}
                  />
                </Box>
              </>
            </Grid>

            {Boolean(formik.values.containerIndex) && (
              <Grid item xs={12} md={6}>
                <Grid item>
                  <FormControlLabel
                    control={
                      <Switch
                        checked={showChassisSelect}
                        onChange={async (e: React.ChangeEvent<HTMLInputElement>) => {
                          setShowChassisSelect(e.target.checked);
                          if (e.target.checked) {
                            await fetchChassisByIndex();
                          } else {
                            setChassisOptions([]);
                            setSelectedChassis([]);
                          }
                        }}
                        name="gateOutCars"
                        color="primary"
                      />
                    }
                    label="Gateout Cars"
                  />
                </Grid>

                {showChassisSelect && (
                  <Grid item xs={12} md={12} style={{ marginTop: "8px" }}>
                    <>
                      <TextField
                        required={allChassisGateOut}
                        className="customTextField"
                        size="small"
                        fullWidth
                        variant="outlined"
                        InputLabelProps={{ shrink: true }}
                        inputProps={{ readOnly: true }} // Prevent manual input
                        //value={formik.values.chassisNumbers || ""} // Display selected chassis
                        label="Chassis Numbers"
                        name="chassisNumbers"
                        autoComplete="off"
                        error={
                          formik.touched.chassisNumbers && Boolean(formik.errors.chassisNumbers)
                        }
                        helperText={formik.touched.chassisNumbers && formik.errors.chassisNumbers}
                      />

                      <Box
                        className={classes.multiselectConfigFileCompression}
                        style={{
                          bottom:
                            formik.touched.chassisNumbers && Boolean(formik.errors.chassisNumbers)
                              ? "55px"
                              : "35px",
                        }}
                      >
                        <MultiSelect2
                          allValues={chassisOptions || []}
                          selctedItems={selectedChassis}
                          displayValue="chasisNumber"
                          placeholder="Select Chassis Numbers"
                          onSelect={(selectedList: any[]) => {
                            setSelectedChassis(selectedList);
                            formik.setFieldValue(
                              "chassisNumbers",
                              selectedList.map((item) => item.chasisNumber).join(", ")
                            );
                          }}
                          onRemove={(selectedList: any[]) => {
                            setSelectedChassis(selectedList);
                            formik.setFieldValue(
                              "chassisNumbers",
                              selectedList.map((item) => item.chasisNumber).join(", ")
                            );
                          }}
                          ref={multiselectRef}
                          key={multiSelectKey}
                        />
                      </Box>
                    </>
                  </Grid>
                )}
              </Grid>
            )}
          </>
        )} */}
      </Grid>

      <GateOutDetails
        vehicleDetails={selectDetails}
        vehiclePaymentsDetails={vehiclePaymentDetails}
        indexContainerDetails={indexContainerDetails}
        terminalType={terminalType}
      />

      <Grid spacing={2} container>
        <Grid item xs={12} style={{ marginTop: "30px" }}>
          <Box>
            <TextField
              className="customTextField"
              size="small"
              fullWidth
              variant="outlined"
              InputLabelProps={{
                shrink: true,
              }}
              label="Remarks"
              name="remarks"
              disabled={false}
              multiline
              rows={2}
              value={formik.values.remarks}
              onChange={handleChange}
              error={formik.touched.remarks && Boolean(formik.errors.remarks)}
              helperText={formik.touched.remarks && formik.errors.remarks}
            />
          </Box>
        </Grid>
        <Grid item container xs={12}>
          <CommonButton
            isGreen
            cssClass={classes.gateOutButton}
            onClick={async () => {
              // Check if payment is completed for selected containers before opening checkout dialog (only for terminalType === 4)
              if (terminalType === 4 && !checkPaymentStatus()) {
                setIsOpenPaymentDialog(true);
                return;
              }

              setIsOpenCheckoutDailog(true);
            }}
          >
            Checkout
          </CommonButton>

          <CommonButton
            cssClass={classes.gateOutButton}
            style={{ marginLeft: "10px" }}
            onClick={() => {
              setIsOpenPrintDailog(true);
            }}
          >
            Print
          </CommonButton>
        </Grid>
      </Grid>

      <TransitionDialog
        isOpen={isOpenCheckoutDailog}
        handleClose={handleCloseCheckoutDialog}
        heading={"Confirmation"}
        handleConfirm={handleCheckoutDialog}
      >
        Are you sure you want to checkout.
      </TransitionDialog>

      <DuplicateReceiptDialog
        isOpen={isOpenPrintDailog}
        handleOriginal={() => {
          printReceipt("0");
        }}
        heading={"Confirmation"}
        handleDuplicate={() => {
          printReceipt("1");
        }}
        onClose={() => {
          setIsOpenPrintDailog(false);
        }}
      >
        Which receipt do you want to print?
      </DuplicateReceiptDialog>

      <TransitionDialog
        isOpen={isOpenPaymentDialog}
        handleClose={() => setIsOpenPaymentDialog(false)}
        heading={"Payment Required"}
        handleConfirm={() => setIsOpenPaymentDialog(false)}
      >
        Please complete the payment before submitting the form.
      </TransitionDialog>
    </>
  );
}
